import { Callout } from "@/components/ui/Callout";
import { BodyText } from "@/components/ui/Text";
import {
  callPanelOpenAtom,
  confirmationOpenAtom,
} from "@/features/AudioVideo/atoms/videoCallAtoms";
import { DailyCallContainer } from "@/features/AudioVideo/components/DailyCallContainer";
import { useJoinCall } from "@/features/AudioVideo/hooks/useJoinCall";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { useRoomToken } from "@/features/Patient/network/useRoomToken";
import { isUniversalClinicPatient } from "@/features/Patient/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useChatInfiniteScroll } from "../hooks/useChatInfiniteScroll";
import { useChatScroll } from "../hooks/useChatScroll";
import { useChatSocketRoom } from "../hooks/useChatSocket";
import { useJumpButton } from "../hooks/useJumpButton";
import { useMessageGrouping } from "../hooks/useMessageGrouping";
import { useChatMessagesInfinite } from "../network/useChatMessagesInfinite";
import {
  ChatFilterMode,
  FILTER_TO_CHAT_ACTION,
} from "../types/chatMessageTypes";
import { ChatFilterTabs } from "./ChatFilterTabs";
import { ChatInputBar } from "./ChatInputBar";
import { ChatLoadingState } from "./ChatLoadingState";
import { ChatMessageContent } from "./ChatMessageContent";
import { ChatModeBanner } from "./ChatModeBanner";
import { JumpToBottomButton } from "./JumpToBottomButton";

// Constants
const PARAM_ROUTE = "/patient/$patientId";
const ERROR_TITLE = "Something went wrong";
const ERROR_DESCRIPTION_PREFIX = "Unable to load messages: ";
const EMPTY_STATE_TEXT = "No Chat Messages";
const ASYNC_DISABLED_FILTERS = [ChatFilterMode.ASYNC, ChatFilterMode.COMBINED];
const NO_DISABLED_FILTERS: ChatFilterMode[] = [];

// Mapping from chatTab query param to ChatFilterMode
const TAB_TO_MODE: Record<string, ChatFilterMode> = {
  live: ChatFilterMode.LIVE,
  async: ChatFilterMode.ASYNC,
  combined: ChatFilterMode.COMBINED,
};

// Mapping from ChatFilterMode to chatTab query param
const MODE_TO_TAB: Record<ChatFilterMode, "live" | "async" | "combined"> = {
  [ChatFilterMode.LIVE]: "live",
  [ChatFilterMode.ASYNC]: "async",
  [ChatFilterMode.COMBINED]: "combined",
};

/**
 * ChatMessageList component
 *
 * The main container component for the chat interface. Orchestrates the following:
 * 1. Fetching chat messages and room information
 * 2. Managing socket connection for real-time updates
 * 3. Handling scrolling behavior and infinite loading
 * 4. Coordinating subcomponents for consistent UI presentation
 * 5. Managing message filtering (Live, Async, Combined)
 *
 * This component serves as a coordinator between the various specialized
 * hooks and components, while keeping its own logic minimal.
 */
export const ChatMessageList = memo(() => {
  // Get patient and encounter IDs from the URL
  const { patientId } = useParams({ from: PARAM_ROUTE });
  const { encounterId, chatTab } = useSearch({ from: PARAM_ROUTE });
  const navigate = useNavigate();
  const location = useLocation();
  const callPanelOpen = useAtomValue(callPanelOpenAtom);
  const confirmationOpen = useAtomValue(confirmationOpenAtom);
  const { data: patient, isLoading: isLoadingPatient } = useGetPatient(
    patientId as string,
  );
  const isAmazonPatient = Boolean(patient?.amazonId);
  const isUCPatient = isUniversalClinicPatient(patient);
  const patientDisplayName = patient?.displayName;

  // Determine if async chat should be disabled for this patient type
  const isAsyncDisabled = isAmazonPatient || isUCPatient;

  // Check if chat filter tabs feature is enabled
  const { data: chatFilterEnabled } = useConfigCatFeatureFlag("decouple-chat");

  // Fetch the room token needed for socket communication and API calls
  const {
    data: roomToken,
    isLoading: isLoadingRoomToken,
    isSuccess: isSuccessRoomToken,
  } = useRoomToken({
    patientId,
    encounterId: encounterId?.toString(),
  });

  const {
    toggleVideoVisitCall,
    isCallPending,
    leaveCall,
    callObject,
    addTranslator,
    removeTranslator,
  } = useJoinCall();

  // Initialize socket connection for real-time chat functionality
  const { typingUser } = useChatSocketRoom(roomToken || "");

  // Initialize filter mode - use chatTab from URL if valid, otherwise default
  const getInitialFilterMode = (): ChatFilterMode => {
    // If patient data is still loading, default to COMBINED as a safe fallback
    // The useEffect below will correct it once patient data loads
    if (isLoadingPatient) {
      return ChatFilterMode.COMBINED;
    }

    if (chatTab) {
      const mode = TAB_TO_MODE[chatTab];
      // If async is disabled and chatTab is async/combined, default to live
      if (isAsyncDisabled && mode !== ChatFilterMode.LIVE) {
        return ChatFilterMode.LIVE;
      }
      return mode || ChatFilterMode.COMBINED;
    }
    // Default to LIVE for UC/Amazon, COMBINED for DTC
    return isAsyncDisabled ? ChatFilterMode.LIVE : ChatFilterMode.COMBINED;
  };

  const [filterMode, setFilterMode] = useState<ChatFilterMode>(
    getInitialFilterMode(),
  );

  // Effect to handle chatTab changes from URL and patient data loading
  useEffect(() => {
    if (isLoadingPatient) return;

    if (chatTab) {
      const mode = TAB_TO_MODE[chatTab];
      if (mode) {
        // Respect disabled filters
        if (isAsyncDisabled && mode !== ChatFilterMode.LIVE) {
          setFilterMode(ChatFilterMode.LIVE);
        } else {
          setFilterMode(mode);
        }
      }
    } else {
      // No chatTab in URL - apply default based on patient type
      // UC/Amazon patients default to LIVE, DTC defaults to COMBINED
      setFilterMode(
        isAsyncDisabled ? ChatFilterMode.LIVE : ChatFilterMode.COMBINED,
      );
    }
  }, [chatTab, isAsyncDisabled, isLoadingPatient]);

  // Handle filter tab changes - update URL to keep it in sync
  const handleFilterChange = useCallback(
    (filter: ChatFilterMode) => {
      navigate({
        to: location.pathname,
        search: (prev) => ({ ...prev, chatTab: MODE_TO_TAB[filter] }),
        replace: true,
      });
    },
    [navigate, location.pathname],
  );

  // Fetch chat messages with infinite scrolling support and server-side filtering
  const {
    data,
    error,
    status,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useChatMessagesInfinite({
    roomToken,
    filterMode,
    enabled: !isLoadingPatient,
  });

  // Determine effective chat mode based on filter
  // Combined always uses LIVE_CHAT - input will be disabled if Live is blocked
  const effectiveChatMode = FILTER_TO_CHAT_ACTION[filterMode];

  // Only pass effectiveChatMode when feature flag is enabled
  // When disabled, ChatInputBar will use chatMode from the API
  const chatModeForInput = chatFilterEnabled ? effectiveChatMode : undefined;

  // Set up scroll behavior for the chat container
  const { scrollContainerRef, storeScrollHeight, scrollToBottom } =
    useChatScroll(data, status);

  // Set up infinite scrolling to load older messages
  const { loadMoreRef } = useChatInfiniteScroll({
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
    storeScrollHeight,
  });

  // Manage the "jump to bottom" button visibility
  const { showJumpButton, bottomRef } = useJumpButton();

  // Extract user information from the API response
  const userMap = useMemo(() => data?.userMap || {}, [data?.userMap]);

  // Process and organize messages by date and sender (using server-filtered messages)
  const dateGroups = useMessageGrouping(data?.messages ?? [], userMap);

  // Show tabs when feature flag is enabled (for ALL patient types now)
  const showFilterTabs = chatFilterEnabled;

  // Compute disabled filters for UC/Amazon patients
  const disabledFilters = isAsyncDisabled
    ? ASYNC_DISABLED_FILTERS
    : NO_DISABLED_FILTERS;

  if (isAmazonPatient && !encounterId) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Callout
          title="Must Select CIQ"
          description="Please select a CIQ from the patient's Timeline to view the chat or start a clinical note."
          variant="warning"
          className="max-w-md"
        />
      </div>
    );
  }

  if (isSuccessRoomToken && !roomToken) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Callout
          title="Inaccessible Medical Record"
          description="This medical record is inaccessible by the patient. No chat messages will be available."
          variant="info"
          className="max-w-md"
        />
      </div>
    );
  }

  // Show loading state while data is being fetched
  if (status === "pending" || isLoadingRoomToken || isLoadingPatient) {
    return <ChatLoadingState />;
  }

  // Show error state if message fetching failed
  if (status === "error") {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Callout
          title={ERROR_TITLE}
          description={`${ERROR_DESCRIPTION_PREFIX}${error.message}`}
          variant="danger"
          className="max-w-md"
        />
      </div>
    );
  }

  // Render the complete chat interface
  return (
    <div className="relative h-full flex flex-col">
      {(callPanelOpen || confirmationOpen) && (
        <DailyCallContainer
          callObject={callObject}
          leaveCall={leaveCall}
          addTranslator={addTranslator}
          removeTranslator={removeTranslator}
        />
      )}

      {/* Filter tabs - now always shown when feature flag enabled */}
      {showFilterTabs && (
        <ChatFilterTabs
          selectedFilter={filterMode}
          onFilterChange={handleFilterChange}
          disabledFilters={disabledFilters}
        />
      )}

      {/* Mode banner - shows what user is viewing */}
      {showFilterTabs && <ChatModeBanner filterMode={filterMode} />}

      {/* Main scrollable chat area with messages */}
      <div
        ref={scrollContainerRef}
        className="flex flex-col-reverse flex-1 overflow-y-auto px-4 py-2 gap-3"
      >
        {dateGroups.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <BodyText tag="p" size="sm" textColor="tertiary">
              {EMPTY_STATE_TEXT}
            </BodyText>
          </div>
        ) : (
          <ChatMessageContent
            encounterId={encounterId?.toString()}
            isCallPending={isCallPending}
            toggleVideoVisitCall={toggleVideoVisitCall}
            dateGroups={dateGroups}
            userMap={userMap}
            hasNextPage={Boolean(hasNextPage)}
            isFetchingNextPage={isFetchingNextPage}
            loadMoreRef={loadMoreRef}
            bottomRef={bottomRef}
            typingUser={Boolean(typingUser)}
            patientDisplayName={patientDisplayName}
          />
        )}
      </div>

      {/* Button to jump to the most recent messages */}
      <JumpToBottomButton
        onClick={scrollToBottom}
        show={showJumpButton && data?.messages.length > 0}
      />

      {/* Input bar for composing and sending messages */}
      <ChatInputBar
        toggleVideoVisitCall={() =>
          toggleVideoVisitCall(encounterId?.toString())
        }
        isCallPending={isCallPending}
        roomToken={roomToken}
        filterMode={filterMode}
        effectiveChatMode={chatModeForInput}
      />
    </div>
  );
});

ChatMessageList.displayName = "ChatMessageList";
